<template>
  <div class="floating-actions" v-if="showFloatingActions">
    <!-- Shop Filters Button (above heart) -->
    <button
      v-if="showShopFiltersBtn"
      type="button"
      class="floating-btn filters-btn"
      title="Φίλτρα"
      aria-label="Άνοιγμα φίλτρων"
      @click="openShopFilters"
    >
      <i class="bi bi-funnel-fill"></i>
      <div class="btn-tooltip">Φίλτρα</div>
    </button>

    <!-- Wishlist Button -->
    <router-link 
      v-if="showWishlist && !isMagazineMode"
      to="/wishlist" 
      class="floating-btn wishlist-btn" 
      title="Λίστα Επιθυμιών"
    >
      <i class="bi bi-heart-fill"></i>
      <span class="floating-badge" v-if="wishlistCount > 0">{{ wishlistCount }}</span>
      <div class="btn-tooltip">Λίστα Επιθυμιών</div>
    </router-link>

    <!-- Cart Button -->
    <button 
      v-if="showCart"
      @click="openCartSidebar" 
      class="floating-btn cart-btn" 
      title="Καλάθι"
    >
      <i class="bi bi-basket-fill"></i>
      <span class="floating-badge" v-if="cartCount > 0">{{ cartCount }}</span>
      <div class="btn-tooltip">Καλάθι</div>
    </button>

    <!-- Back to Top Button with Progress -->
    <button 
      v-if="showScrollProgress"
      @click="scrollToTop" 
      class="floating-btn scroll-btn" 
      title="Επιστροφή στην Κορυφή"
    >
      <!-- SVG Progress Ring -->
      <svg class="progress-ring" width="56" height="56" viewBox="0 0 56 56">
        <circle
          class="progress-ring-bg"
          stroke="rgba(255, 255, 255, 0.3)"
          stroke-width="3"
          fill="transparent"
          r="24"
          cx="28"
          cy="28"
        />
        <circle
          class="progress-ring-circle"
          :stroke="progressColor"
          stroke-width="3"
          fill="transparent"
          r="24"
          cx="28"
          cy="28"
          :style="{ strokeDashoffset: circumference - (scrollPercentage / 100) * circumference }"
        />
      </svg>
      
      <!-- Arrow Icon -->
      <i class="bi bi-arrow-up"></i>
      
      <!-- Percentage on Hover -->
      <div class="scroll-percentage">{{ Math.round(scrollPercentage) }}%</div>
      <div class="btn-tooltip">Επιστροφή στην Κορυφή</div>
    </button>
  </div>

  <!-- Linear Progress Bar (Top) -->
  <div v-if="showScrollProgress" class="scroll-progress-bar">
    <div 
      class="scroll-progress-bar-fill" 
      :style="{ width: scrollPercentage + '%', background: progressGradient }"
    ></div>
  </div>

  <!-- Cart Sidebar -->
  <CartSidebar v-if="showCart" :isOpen="isCartSidebarOpen" @close="closeCartSidebar" />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useWishlistStore } from '@/stores/wishlist'
import { useCartStore } from '@/stores/cart'
import { useSiteStyle } from '@/composables/useSiteStyle'
import CartSidebar from './CartSidebar.vue'

const route = useRoute()
const wishlistStore = useWishlistStore()
const cartStore = useCartStore()
const { showCart, showWishlist, loadSiteStyle, isMagazineMode, isToursMode } = useSiteStyle()

// Load site style on component mount
onMounted(() => {
  loadSiteStyle()
  window.addEventListener('scroll', updateScrollProgress)
  updateScrollProgress()
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateScrollProgress)
})

// Sidebar state
const isCartSidebarOpen = ref(false)

// Scroll progress state
const scrollPercentage = ref(0)
const showScrollProgress = ref(false)

// SVG circle calculations
const radius = 24
const circumference = 2 * Math.PI * radius

// Store counts
const cartCount = computed(() => cartStore.itemCount)
const wishlistCount = computed(() => wishlistStore.wishlistCount)

// Dynamic color based on scroll percentage
const progressColor = computed(() => {
  if (scrollPercentage.value < 25) return '#0d6efd' // Blue
  if (scrollPercentage.value < 50) return '#0dcaf0' // Cyan
  if (scrollPercentage.value < 75) return '#198754' // Green
  return '#fd7e14' // Orange
})

const progressGradient = computed(() => {
  if (scrollPercentage.value < 25) {
    return 'linear-gradient(90deg, #0d6efd, #0dcaf0)'
  } else if (scrollPercentage.value < 50) {
    return 'linear-gradient(90deg, #0dcaf0, #198754)'
  } else if (scrollPercentage.value < 75) {
    return 'linear-gradient(90deg, #198754, #ffc107)'
  }
  return 'linear-gradient(90deg, #ffc107, #fd7e14)'
})

// Show floating actions only on certain pages (not on cart/wishlist pages themselves)
const showFloatingActions = computed(() => {
  const currentPath = route.path
  
  // Don't show on admin pages
  if (currentPath.startsWith('/admin')) {
    return false
  }
  
  // Don't show on cart or wishlist pages
  if (currentPath === '/cart' || currentPath === '/wishlist') {
    return false
  }
  
  // Don't show on checkout page
  if (currentPath === '/checkout') {
    return false
  }
  
  // Show on all other pages
  return true
})

const isShopPage = computed(() => route.path === '/shop' || route.path.startsWith('/shop/'))

// Filters btn: shop only, after user has scrolled (same threshold as scroll-to-top)
const showShopFiltersBtn = computed(() => isShopPage.value && showScrollProgress.value)

const openShopFilters = () => {
  window.dispatchEvent(new CustomEvent('shop-open-filters'))
}

// Scroll progress update
const updateScrollProgress = () => {
  const windowHeight = window.innerHeight
  const documentHeight = document.documentElement.scrollHeight
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  
  const scrollableHeight = documentHeight - windowHeight
  const scrolled = (scrollTop / scrollableHeight) * 100
  
  scrollPercentage.value = Math.min(100, Math.max(0, scrolled))
  showScrollProgress.value = scrollTop > 200 // Show after scrolling 200px
}

// Scroll to top
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

// Methods
const openCartSidebar = () => {
  isCartSidebarOpen.value = true
}

const closeCartSidebar = () => {
  isCartSidebarOpen.value = false
}
</script>

<style scoped>
.floating-actions {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none; /* Allow clicks to pass through the container */
}

.floating-btn {
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto; /* Re-enable clicks for buttons */
  font-size: 1.2rem;
  border: 3px solid #fff;
  backdrop-filter: blur(10px);
}

.floating-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  text-decoration: none;
}

.floating-btn:active {
  transform: translateY(0);
}

/* Wishlist Button */
.wishlist-btn {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  color: white;
}

.wishlist-btn:hover {
  background: linear-gradient(135deg, #c0392b 0%, #a93226 100%);
  color: white;
}

/* Shop Filters Button */
.filters-btn {
  background: linear-gradient(135deg, #0C5A85 0%, #094a6e 100%);
  color: white;
  border: 3px solid #fff;
  cursor: pointer;
}

.filters-btn:hover {
  background: linear-gradient(135deg, #094a6e 0%, #073a57 100%);
  color: white;
  text-decoration: none;
}

/* Cart Button */
.cart-btn {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  color: white;
  border: 3px solid #fff;
  cursor: pointer;
}

.cart-btn:hover {
  background: linear-gradient(135deg, #2980b9 0%, #1f618d 100%);
  color: white;
  text-decoration: none;
}

/* Scroll to Top Button */
.scroll-btn {
  background: linear-gradient(135deg, #8e44ad 0%, #6c3483 100%);
  color: white;
  border: 3px solid #fff;
  cursor: pointer;
  position: relative;
  overflow: visible;
}

.scroll-btn:hover {
  background: linear-gradient(135deg, #6c3483 0%, #5b2c6f 100%);
  color: white;
  text-decoration: none;
}

.scroll-btn i {
  position: relative;
  z-index: 2;
  font-size: 1.3rem;
  transition: all 0.3s ease;
}

.scroll-btn:hover i {
  transform: translateY(-3px);
}

/* SVG Progress Ring */
.progress-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1; /* Behind the icon */
}

.progress-ring-bg {
  opacity: 0.3;
}

.progress-ring-circle {
  stroke-dasharray: 150.8; /* 2 * PI * 24 */
  stroke-dashoffset: 150.8;
  transition: stroke-dashoffset 0.3s ease, stroke 0.3s ease;
  stroke-linecap: round;
}

/* Scroll Percentage */
.scroll-percentage {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 3;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 5px;
  border-radius: 8px;
}

.scroll-btn:hover .scroll-percentage {
  opacity: 1;
}

.scroll-btn:hover i {
  opacity: 0;
}

/* Linear Progress Bar (Top) */
.scroll-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(0, 0, 0, 0.05);
  z-index: 9998;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.scroll-progress-bar {
  opacity: 1;
}

.scroll-progress-bar-fill {
  height: 100%;
  transition: width 0.3s ease, background 0.5s ease;
  box-shadow: 0 0 10px rgba(142, 68, 173, 0.5);
}

/* Badge */
.floating-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  color: white;
  border: 2px solid #fff;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: bold;
  min-width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(243, 156, 18, 0.4);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

/* Tooltip */
.btn-tooltip {
  position: absolute;
  right: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  pointer-events: none;
}

.btn-tooltip::after {
  content: '';
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-left-color: rgba(0, 0, 0, 0.8);
}

.floating-btn:hover .btn-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateY(-50%) translateX(-5px);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .floating-actions {
    right: 15px;
    bottom: 80px; /* Move higher to avoid mobile browser UI */
    gap: 10px;
    z-index: 1000; /* Increase z-index for mobile */
  }
  
  .floating-btn {
    width: 50px;
    height: 50px;
    font-size: 1.1rem;
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3); /* Stronger shadow for mobile */
  }
  
  .floating-badge {
    min-width: 20px;
    height: 20px;
    font-size: 0.65rem;
    top: -4px;
    right: -4px;
  }
  
  /* Hide tooltips on mobile to avoid UI clutter */
  .btn-tooltip {
    display: none;
  }
}

@media (max-width: 480px) {
  .floating-actions {
    right: 12px;
    bottom: 70px; /* Higher position for smaller screens */
    z-index: 1001; /* Even higher z-index */
  }
  
  .floating-btn {
    width: 48px;
    height: 48px;
    font-size: 1rem;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4); /* Even stronger shadow */
  }
  
  .floating-badge {
    min-width: 18px;
    height: 18px;
    font-size: 0.6rem;
  }
}

/* Animation for entrance */
.floating-btn {
  animation: slideInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.wishlist-btn {
  animation-delay: 0.1s;
}

.cart-btn {
  animation-delay: 0.2s;
}

.scroll-btn {
  animation-delay: 0.3s;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(60px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Ensure floating buttons don't interfere with page content */
.floating-actions {
  /* Make sure it doesn't block clicks on page content */
  pointer-events: none;
}

.floating-btn {
  /* Re-enable clicks only for the buttons */
  pointer-events: auto;
}

/* Additional mobile optimizations */
@media (max-width: 768px) {
  /* Ensure visibility on all mobile screens */
  .floating-actions {
    bottom: 80px !important; /* Force position above mobile browser UI */
    right: 15px !important;
    z-index: 1000 !important;
  }
  
  /* Make sure buttons are always visible */
  .floating-btn {
    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%) !important;
    border: 3px solid #fff !important;
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4) !important;
  }
  
  .wishlist-btn {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%) !important;
  }
}

/* Very small screens - make buttons smaller but keep them visible */
@media (max-width: 320px) {
  .floating-actions {
    right: 8px !important;
    bottom: 60px !important;
    gap: 8px;
    z-index: 1002 !important;
    /* Debug: Add a background to see if container is there */
    /* background: rgba(255, 0, 0, 0.1); */
  }
  
  .floating-btn {
    width: 44px;
    height: 44px;
    font-size: 0.9rem;
    /* Force visibility */
    display: flex !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
  
  .floating-badge {
    min-width: 16px;
    height: 16px;
    font-size: 0.55rem;
    top: -3px;
    right: -3px;
  }
}

/* Special handling for iOS Safari bottom bar */
@supports (padding: max(0px)) {
  @media (max-width: 768px) {
    .floating-actions {
      bottom: max(20px, env(safe-area-inset-bottom, 20px));
    }
  }
}
</style>
